import React, { useEffect, useState } from 'react';
import {
  globalModalManager,
  type GlobalModalState,
  type DeletedProjectModalData,
  type ProjectNameMismatchModalData,
  type ValidationErrorModalData,
  type ExistingProjectWarningModalData,
} from '../../../services/globalModalService';
import DeletedProjectWarningModal from '../modals/DeletedProjectWarningModal';
import ProjectNameConfirmationModal from '../modals/ProjectNameConfirmationModal ';
import ExistingProjectWarningModal from '../modals/ExistingProjectWarningModal';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
} from '@mui/material';

/**
 * GlobalModalContainer
 * Renders modals that need to appear across pages during background tasks
 * This component should be mounted at the App level
 */
const GlobalModalContainer: React.FC = () => {
  const [modalState, setModalState] = useState<GlobalModalState>(
    globalModalManager.getState()
  );
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    // Subscribe to modal state changes
    const unsubscribe = globalModalManager.subscribe((state) => {
      setModalState(state);
      // Reset processing state when modal changes
      if (!state.isOpen) {
        setProcessing(false);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  if (!modalState.isOpen || !modalState.data) {
    return null;
  }

  // Render deleted project warning modal
  if (modalState.type === 'deleted-project-warning') {
    const data = modalState.data as DeletedProjectModalData;
    
    return (
      <DeletedProjectWarningModal
        open={modalState.isOpen}
        projectName={data.projectName}
        deletedAt={data.deletedAt}
        deletedByName={data.deletedByName}
        loading={processing}
        onConfirm={async () => {
          setProcessing(true);
          try {
            await data.onConfirm();
            globalModalManager.closeModal();
          } catch (error) {
            console.error('Error confirming deleted project:', error);
          } finally {
            setProcessing(false);
          }
        }}
        onCancel={() => {
          data.onCancel();
          globalModalManager.closeModal();
        }}
      />
    );
  }

  // Render project name mismatch modal
  if (modalState.type === 'project-name-mismatch') {
    const data = modalState.data as ProjectNameMismatchModalData;
    
    return (
      <ProjectNameConfirmationModal
        open={modalState.isOpen}
        originalProjectName={data.originalProjectName}
        fileProjectName={data.fileProjectName}
        loading={processing}
        onClose={() => {
          data.onCancel();
          globalModalManager.closeModal();
        }}
        onChooseOriginal={async () => {
          setProcessing(true);
          try {
            await data.onChooseOriginal();
            globalModalManager.closeModal();
          } catch (error) {
            console.error('Error choosing original project name:', error);
          } finally {
            setProcessing(false);
          }
        }}
        onChooseNew={async () => {
          setProcessing(true);
          try {
            await data.onChooseNew();
            globalModalManager.closeModal();
          } catch (error) {
            console.error('Error choosing new project name:', error);
          } finally {
            setProcessing(false);
          }
        }}
      />
    );
  }

  // Render validation error modal
  if (modalState.type === 'validation-error') {
    const data = modalState.data as ValidationErrorModalData;
    
    return (
      <Dialog
        open={modalState.isOpen}
        onClose={() => {
          data.onClose();
          globalModalManager.closeModal();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Validation Error</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {data.errorType.replace(/_/g, ' ').toUpperCase()}
          </Alert>
          <Typography variant="body1">
            {data.errorMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              data.onClose();
              globalModalManager.closeModal();
            }}
            variant="contained"
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Render existing project warning modal
  if (modalState.type === 'existing-project-warning') {
    const data = modalState.data as ExistingProjectWarningModalData;
    
    return (
      <ExistingProjectWarningModal
        open={modalState.isOpen}
        projectName={data.projectName}
        bcName={data.bcName}
        gpm={data.gpm}
        bcId={data.bcId}
        onCancel={() => {
          data.onCancel();
          globalModalManager.closeModal();
        }}
      />
    );
  }

  return null;
};

export default GlobalModalContainer;
